import React, { useMemo } from 'react';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { MissingMonsterPhotos } from './MissingMonsterPhotos';
import { MonsterPhotoAudit } from './MonsterPhotoAudit';
import { NpcPhotoPackCheck } from './NpcPhotoPackCheck';
import { resolveImageEntries, useFailedImages } from '../../utils/entityImages';
import { unassignedImages } from '../../data/guide/entityImages';
import type { WorldData } from '../../types/world';

/** Panel del staff: estado de las imágenes, separado de drops, estadísticas, recetas y ubicaciones. */
export function ImageCheck({
  w
}: {
  w: WorldData;
}) {
  const {
    resolved
  } = useMemo(() => resolveImageEntries(w), [w]);
  const failed = useFailedImages();
  const rows = resolved.map(e => {
    const src = e.image?.src;
    const status = !src ? 'Sin imagen' : failed.includes(src) ? 'No carga' : e.image?.needsConfirmation ? 'Integrada · confirmar asociación' : 'Integrada';
    const id = e.kind === 'clase' ? e.classId?.toUpperCase() : e.resolvedMonsterIds.length ? e.resolvedMonsterIds.join(', ') : '—';
    const idNote = e.notInCatalog ? 'no está en 06_monsters: no se muestra' : e.ambiguous ? 'nombre con varios IDs: no se asigna' : '';
    return {
      e,
      status,
      id,
      idNote
    };
  });
  const pendingText = [...rows.filter(r => r.status !== 'Integrada').map(r => `${r.e.label} (${r.e.kind}, ID ${r.id}${r.idNote ? `, ${r.idNote}` : ''}) · ${r.status}${r.e.pending ? ` · ${r.e.pending}` : ''}`), ...unassignedImages.map(u => `Imagen sin asignar · ${u.description} · ${u.question} · ${u.src}`)].join('\n');
  return <WorldSection id="imagenes" title="Imágenes de clases, NPC y monstruos" count={rows.length} data-id="element-1772" data-mp="1olh7yue1v3cd">
      <p className="mb-3 max-w-3xl text-sm text-gray-400" data-id="element-1773" data-mp="1smkjx5sz8f8b">
        Imagen proporcionada por el staff: identifica a la entidad, no confirma drops, coordenadas, estadísticas ni recetas. Si una imagen no carga, aparece aquí al intentar mostrarla en este navegador.
      </p>
      <MonsterPhotoAudit w={w} data-id="element-1774" data-mp="11mbh6cq61gxx" />
      <NpcPhotoPackCheck w={w} data-id="element-1775" data-mp="wmovdew9usdi" />
      <MissingMonsterPhotos w={w} data-id="element-1776" data-mp="wni7kve02a31" />
      <div className="mb-3" data-id="element-1777" data-mp="wojhgwja4qj6">
        <CopyTextButton text={pendingText || 'Sin imágenes pendientes.'} label="Copiar imágenes pendientes" data-id="element-1778" data-mp="1amhvg0touky5" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10" data-id="element-1779" data-mp="16kd1t238pobi">
        <table className="w-full min-w-[720px] text-left text-sm" data-id="element-1780" data-mp="1kknv0bs0vezu">
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1781" data-mp="1kit8wu50lgu6">
            <tr data-id="element-1782" data-mp="1fkjcyhbguc7i">
              <th className="px-4 py-3 font-semibold" data-id="element-1783" data-mp="1fl1hs2c71yr1">Imagen</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1784" data-mp="1uayxdtw25g4m">Entidad</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1785" data-mp="1uca0drhoijuo">ID</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1786" data-mp="1pcp1ti6f8dih">Estado</th>
              <th className="px-4 py-3 font-semibold" data-id="element-1787" data-mp="23am0ql8ckw0b">Procedencia y asociación</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-1788" data-mp="1ycu1151go5lk">
            {rows.map(({
            e,
            status,
            id,
            idNote
          }) => <tr key={e.key} data-id="element-1789" data-mp="2hz5mc4plx7cd">
                <td className="px-4 py-3" data-id="element-1790" data-mp="2hvrgunlnci0p"><EntityImage entry={e} alt={e.label} kind={e.kind === 'monstruo' ? 'monstruo' : 'persona'} data-id="element-1791" data-mp="2mwoz106npc27" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1792" data-mp="28xpartn22z6k">{e.label}<span className="block text-xs text-gray-500" data-id="element-1793" data-mp="28wwclh25xi01">{e.kind}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1794" data-mp="2cxg70cvcq7ih">{id}{idNote && <span className="block font-sans text-xs text-amber-100" data-id="element-1795" data-mp="1z6umyku5vrj0">{idNote}</span>}</td>
                <td className="px-4 py-3 text-gray-200" data-id="element-1796" data-mp="23xt75vi9t9rj">{status}{e.pending && <span className="block text-xs text-gray-500" data-id="element-1797" data-mp="23ze1bk0oaqov">{e.pending}</span>}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1798" data-mp="1parnxnulw90a">{e.image ? <>Imagen proporcionada por el staff · {e.image.addedOn}<span className="block" data-id="element-1799" data-mp="1u8jg8iz5xx50">{e.image.matchedBy}</span></> : '—'}</td>
              </tr>)}
            {unassignedImages.map(u => <tr key={u.src} data-id="element-1800" data-mp="1l7uzhgidxiof">
                <td className="px-4 py-3" data-id="element-1801" data-mp="1l6s8l8l2bg8i"><img src={u.src} alt={u.description} loading="lazy" className="h-10 w-10 rounded-lg border border-white/10 object-cover object-[50%_30%]" data-id="element-1802" data-mp="1l9eel3sb1nom" /></td>
                <td className="px-4 py-3 text-white" data-id="element-1803" data-mp="18940iy7obdmv">Sin asignar<span className="block text-xs text-gray-500" data-id="element-1804" data-mp="13h2ondef3oxd">{u.description}</span></td>
                <td className="px-4 py-3 font-mono text-gray-200" data-id="element-1805" data-mp="13epyicyia898">—</td>
                <td className="px-4 py-3 text-amber-100" data-id="element-1806" data-mp="zez9adomo06t">{u.question}</td>
                <td className="px-4 py-3 text-xs text-gray-400" data-id="element-1807" data-mp="uhfkwky7u715">Imagen proporcionada por el staff · no se muestra a los jugadores</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </WorldSection>;
}